/**
 * useGamification — React hook for readiness score and quest status
 *
 * Fetches the authenticated user's gamification state from the backend.
 * Returns readiness (0-100), quests, achievements, and helpers.
 * Caches in sessionStorage to avoid redundant API calls on re-renders.
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../utils/config';
import { getUser } from '../utils/helpers';

const CACHE_KEY = 'slam_gamification_cache';
const CACHE_TTL = 60_000; // 1 minute

function getCached() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached._ts > CACHE_TTL) return null;
    return cached;
  } catch { return null; }
}

export default function useGamification() {
  const [state, setState] = useState(() => {
    const cached = getCached();
    return {
      readiness: cached?.readiness ?? 0,
      quests: cached?.quests ?? [],
      completedCount: cached?.completedCount ?? 0,
      totalCount: cached?.totalCount ?? 5,
      xp: cached?.xp ?? 0,
      onboardingComplete: cached?.onboardingComplete ?? false,
      achievements: cached?.achievements ?? [],
      loading: !cached,
      error: null,
    };
  });

  const user = getUser();
  const userId = user?.id;

  const fetchReadiness = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await axios.get(`${config.SERVER_BASE_URL}/api/gamification/readiness`, {
        headers: { 'x-user-id': userId },
      });
      if (res.data?.success) {
        const data = {
          readiness: res.data.readiness,
          quests: res.data.quests,
          completedCount: res.data.completedCount,
          totalCount: res.data.totalCount,
          xp: res.data.xp,
          onboardingComplete: res.data.onboardingComplete,
        };
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, _ts: Date.now() }));
        setState(prev => ({ ...prev, ...data, loading: false, error: null }));
      }
    } catch (err) {
      setState(prev => ({ ...prev, loading: false, error: err.message }));
    }
  }, [userId]);

  const completeQuest = useCallback(async (questId) => {
    if (!userId) return null;
    try {
      const res = await axios.post(`${config.SERVER_BASE_URL}/api/gamification/quest/complete`, {
        questId,
      }, {
        headers: { 'x-user-id': userId },
      });
      if (res.data?.success) {
        sessionStorage.removeItem(CACHE_KEY);
        await fetchReadiness();
        return res.data;
      }
    } catch (err) {
      console.warn('Quest completion failed:', err.message);
    }
    return null;
  }, [userId, fetchReadiness]);

  const resetOnboarding = useCallback(async () => {
    if (!userId) return null;
    try {
      const res = await axios.post(`${config.SERVER_BASE_URL}/api/gamification/reset`, {}, {
        headers: { 'x-user-id': userId },
      });
      if (res.data?.success) {
        // Clear sessionStorage cache
        sessionStorage.removeItem(CACHE_KEY);
        // Clear sport selection
        localStorage.removeItem('slam_sport_selected');
        localStorage.removeItem('slam_sport_selection');
        // Clear tooltip tour keys
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('slam_tour_')) localStorage.removeItem(key);
        });
        // Reset local state
        setState({
          readiness: 0,
          quests: [],
          completedCount: 0,
          totalCount: 5,
          xp: 0,
          onboardingComplete: false,
          achievements: [],
          loading: false,
          error: null,
        });
        return res.data;
      }
    } catch (err) {
      console.warn('Onboarding reset failed:', err.message);
    }
    return null;
  }, [userId]);

  useEffect(() => {
    fetchReadiness();
  }, [fetchReadiness]);

  return {
    ...state,
    refetch: fetchReadiness,
    completeQuest,
    resetOnboarding,
    isReady: (threshold) => state.readiness >= threshold,
  };
}
